/**
 * Before/After comparison slider custom element.
 * Expects markup with data-ref="before-image", "slider", "handle".
 */
class BeforeAfterComponent extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.beforeImage = this.querySelector('[data-ref="before-image"]');
    this.slider = this.querySelector('[data-ref="slider"]');
    this.handle = this.querySelector('[data-ref="handle"]');
    this.container = this.querySelector('.before-after-block__container');

    if (!this.beforeImage || !this.slider || !this.container) return;

    const initial = Number(this.dataset.defaultPosition || 50);
    this.position = Number.isFinite(initial) ? Math.min(100, Math.max(0, initial)) : 50;
    this.dragging = false;

    this.setPosition(this.position);

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.slider.addEventListener('pointerdown', this.onPointerDown);
    this.container.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);

    if (this.handle) {
      this.handle.setAttribute('role', 'slider');
      this.handle.setAttribute('tabindex', '0');
      this.handle.setAttribute('aria-valuemin', '0');
      this.handle.setAttribute('aria-valuemax', '100');
      this.handle.setAttribute('aria-valuenow', String(Math.round(this.position)));
      this.handle.setAttribute('aria-label', 'Before and after comparison');
      this.handle.addEventListener('keydown', this.onKeyDown);
    }
  }

  disconnectedCallback() {
    this.slider?.removeEventListener('pointerdown', this.onPointerDown);
    this.container?.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    this.handle?.removeEventListener('keydown', this.onKeyDown);
  }

  onPointerDown(event) {
    if (event.button != null && event.button !== 0) return;
    this.dragging = true;
    this.container.setPointerCapture?.(event.pointerId);
    this.updateFromEvent(event);
    event.preventDefault();
    event.stopPropagation();
  }

  onPointerMove(event) {
    if (!this.dragging) return;
    this.updateFromEvent(event);
  }

  onPointerUp() {
    this.dragging = false;
  }

  onKeyDown(event) {
    let next = this.position;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next -= 5;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next += 5;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = 100;
    else return;
    event.preventDefault();
    this.setPosition(next);
  }

  updateFromEvent(event) {
    const rect = this.container.getBoundingClientRect();
    if (!rect.width) return;
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    this.setPosition(x);
  }

  setPosition(value) {
    this.position = Math.min(100, Math.max(0, value));
    const pct = this.position;
    this.beforeImage.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
    this.slider.style.left = `${pct}%`;
    if (this.handle) {
      this.handle.setAttribute('aria-valuenow', String(Math.round(pct)));
    }
  }
}

if (!customElements.get('before-after-component')) {
  customElements.define('before-after-component', BeforeAfterComponent);
}
